'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus } from 'lucide-react';
import { EASE_OUT, EASE_CUSTOM } from '@/lib/animations'

const faqs = [
    {
        id: 1,
        question: '¿Qué incluye el diagnóstico gratuito exactamente?',
        answer: 'Analizamos tu negocio: cómo trabajas, qué herramientas usas, dónde pierdes tiempo y dinero. Puede ser por videollamada (45-60 min), por formulario detallado o sin reuniones (vídeo explicativo por WhatsApp). En 24-48h recibes un informe con problemas detectados, propuesta de solución y ROI proyectado. Sin venta agresiva.'
    },
    {
        id: 2,
        question: '¿Cuáles son las tres formas de empezar?',
        answer: 'Premium: videollamada de diagnóstico contigo, propuesta personalizada en 48h. Express: rellenas el formulario detallado y te enviamos propuesta por email en 24h, sin reuniones obligatorias. Asíncrono: formulario + vídeo explicativo por WhatsApp + PDF firmable + pago online, totalmente sin reuniones. Tú eliges cómo prefieres trabajar.'
    },
    {
        id: 3,
        question: '¿Solo hacéis software o también asesoráis?',
        answer: 'Las dos cosas, y es lo que nos hace diferentes. Primero analizamos tu negocio como un consultor. Después implementamos la solución como un equipo técnico. El mismo equipo que diagnostica es el que construye. Sin intermediarios ni subcontratas.'
    },
    {
        id: 4,
        question: '¿Cuánto cuesta un proyecto?',
        answer: 'No tenemos tarifario rígido — cada propuesta sale del análisis de tu negocio. Como referencia, las implementaciones empiezan desde 1.200€ para automatizaciones sencillas y pueden superar los 25.000€ para sistemas complejos. Siempre con precio fijo pactado antes de empezar y ROI proyectado a 6/12/24 meses. Tú decides cuánto invertir.'
    },
    {
        id: 5,
        question: '¿Las facturas son oficiales? ¿Cumplen con Verifactu?',
        answer: 'Sí. Emitimos facturas oficiales con cumplimiento del Reglamento Verifactu (RD 1007/2023) usando Holded como software de facturación certificado. Recibirás factura formal con todos los datos legales para tu contabilidad. Cumplimos también RGPD y nuestros procesos están alineados con ISO 9001 e ISO 27001.'
    },
    {
        id: 6,
        question: '¿Tenéis experiencia en mi sector?',
        answer: 'Trabajamos con cualquier tipo de negocio: hostelería, gestorías, construcción, clínicas, formación, e-commerce, peluquerías y más. Como consultora, lo primero no es ser experto en tu sector — es entender cómo funciona tu negocio en concreto. Cada empresa tiene sus propios cuellos de botella y nuestro trabajo es encontrarlos antes de proponer nada.'
    },
    {
        id: 7,
        question: '¿Qué pasa si ya tengo algo desarrollado?',
        answer: 'Mejor. Analizamos lo que ya tienes, identificamos qué funciona y qué no, y proponemos la mejor estrategia: mejorar lo existente o construir sobre ello. No tiramos a la basura tu inversión anterior.'
    },
    {
        id: 8,
        question: '¿Qué soporte hay después de la entrega?',
        answer: 'Tras el deploy te ofrecemos acompañamiento mensual desde 49€/mes (sin permanencia obligatoria) que incluye mantenimiento, soporte y mejoras continuas. Para clientes activos hay un canal único de soporte por email (soporte@innovatheratech.com) y WhatsApp con SLA claro: bug crítico < 4h, bug funcional < 24h, mejora o consulta < 48h. Más detalles en la página de Soporte.'
    },
    {
        id: 9,
        question: '¿Hay permanencia obligatoria?',
        answer: 'No. En el acompañamiento mensual te quedas porque los resultados lo justifican, no por un contrato. Puedes salir cuando quieras sin penalizaciones.'
    },
];

export default function FAQ() {
    const [openId, setOpenId] = useState<number | null>(null);
    const toggle = (id: number) => setOpenId(prev => prev === id ? null : id);

    return (
        <section className="py-20 md:py-28 relative z-10">
            <div className="max-w-3xl mx-auto px-6 lg:px-8">

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, ease: EASE_CUSTOM }}
                    className="mb-14 text-center"
                >
                    <div className="text-xs font-mono tracking-[0.3em] text-slate-400 uppercase mb-4">
                        Preguntas frecuentes
                    </div>
                    <h2 className="text-2xl sm:text-4xl md:text-5xl font-black tracking-tight text-slate-900">
                        Respuestas directas.
                        <br />
                        <span className="text-slate-400 font-light">Sin rodeos.</span>
                    </h2>
                </motion.div>

                <div className="flex flex-col gap-3">
                    {faqs.map((faq, i) => {
                        const isOpen = openId === faq.id;
                        return (
                            <motion.div
                                key={faq.id}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.06 }}
                                className={`rounded-2xl border overflow-hidden transition-all duration-300 ${
                                    isOpen
                                        ? 'border-blue-200/60 bg-blue-50/40'
                                        : 'border-slate-200/60 bg-white/60 hover:bg-white/80'
                                }`}
                            >
                                <button
                                    onClick={() => toggle(faq.id)}
                                    className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                                >
                                    <span className={`font-semibold text-sm transition-colors ${isOpen ? 'text-blue-800' : 'text-slate-800'}`}>
                                        {faq.question}
                                    </span>
                                    <div className={`shrink-0 w-7 h-7 rounded-lg flex items-center justify-center border transition-all duration-300 ${
                                        isOpen
                                            ? 'border-blue-300 bg-blue-100 rotate-45'
                                            : 'border-slate-200 bg-white'
                                    }`}>
                                        <Plus className={`w-3.5 h-3.5 transition-colors ${isOpen ? 'text-blue-600' : 'text-slate-400'}`} />
                                    </div>
                                </button>
                                <div
                                    className="overflow-hidden transition-[grid-template-rows] duration-300 ease-out"
                                    style={{ display: 'grid', gridTemplateRows: isOpen ? '1fr' : '0fr' }}
                                >
                                    <div className="min-h-0">
                                        <div className="px-6 pb-6">
                                            <div className="h-px bg-gradient-to-r from-blue-200/50 to-transparent mb-4" />
                                            <p className="text-slate-600 text-sm leading-relaxed font-light">{faq.answer}</p>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-5 rounded-2xl border border-slate-200/60 bg-white/60">
                    <div>
                        <p className="text-slate-800 font-semibold text-sm">¿No encuentras tu respuesta?</p>
                        <p className="text-slate-500 text-sm">Escríbenos y respondemos en menos de 24h.</p>
                    </div>
                    <a href="#contacto"
                        className="shrink-0 flex items-center gap-2 px-5 py-3 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-all active:scale-95 shadow-[0_4px_12px_rgba(37,99,235,0.25)]">
                        Contactar →
                    </a>
                </div>
            </div>
        </section>
    );
}
